import { ThreeEvent } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import * as THREE from "three";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";
import { SELECTED_HANDLE_INTERACTION_Z, SELECTED_HANDLE_RENDER_ORDER } from "../../constants/canvas";
import { editorStore } from "../../store/editorStore";
import { getWallAddButtonRadius } from "../../utils/wallHoverAddButton";

type WallAddHandleProps = {
  position: THREE.Vector2;
  /** Wall thickness used for the world-space hit target. */
  wallWidth: number;
  onAdd: () => void;
  onHoverChange?: (hovered: boolean) => void;
};

/** On-screen chip — large enough to click reliably. */
const CHIP_SIZE_PX = 28;
const ICON_SIZE_PX = 24;

/**
 * Midpoint add-wall affordance using Font Awesome free `faCirclePlus`.
 * Parked at the wall center after a hover pause (position owned by parent).
 */
export const WallAddHandle = (props: WallAddHandleProps) => {
  const { position, wallWidth, onAdd, onHoverChange } = props;
  const [hovered, setHovered] = useState(false);
  const hitRadius = getWallAddButtonRadius(wallWidth, editorStore.zoomLevelDivisor());

  const handleAdd = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    onAdd();
  };

  const handleEnter = () => {
    setHovered(true);
    onHoverChange?.(true);
    document.body.style.cursor = "pointer";
  };

  const handleLeave = () => {
    setHovered(false);
    onHoverChange?.(false);
    if (document.body.style.cursor === "pointer") {
      document.body.style.cursor = "auto";
    }
  };

  return (
    <group
      position={[position.x, position.y, SELECTED_HANDLE_INTERACTION_Z + 0.04]}
      renderOrder={SELECTED_HANDLE_RENDER_ORDER + 1}
    >
      <mesh
        onPointerDown={(event: ThreeEvent<PointerEvent>) => {
          event.stopPropagation();
          handleAdd(event);
        }}
        onPointerEnter={(event: ThreeEvent<PointerEvent>) => {
          event.stopPropagation();
          handleEnter();
        }}
        onPointerLeave={(event: ThreeEvent<PointerEvent>) => {
          event.stopPropagation();
          handleLeave();
        }}
      >
        <circleGeometry args={[hitRadius, 24]} />
        <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
      </mesh>
      <Html
        center
        style={{ pointerEvents: "auto" }}
        zIndexRange={[100, 0]}
      >
        <button
          type="button"
          aria-label="Add wall from midpoint"
          onPointerDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleAdd(event);
          }}
          onPointerEnter={handleEnter}
          onPointerLeave={handleLeave}
          style={{
            width: CHIP_SIZE_PX,
            height: CHIP_SIZE_PX,
            padding: 0,
            border: "none",
            borderRadius: "9999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "0 1px 4px rgba(0,0,0,0.28)",
            color: hovered ? "#0000cc" : CANVAS_ACCENT_COLOR,
            lineHeight: 0,
            cursor: "pointer",
            transform: hovered ? "scale(1.08)" : "scale(1)",
            transition: "transform 80ms ease-out, color 80ms ease-out",
          }}
        >
          <FontAwesomeIcon
            icon={faCirclePlus}
            style={{ fontSize: ICON_SIZE_PX, width: ICON_SIZE_PX, height: ICON_SIZE_PX }}
          />
        </button>
      </Html>
    </group>
  );
};
